import React, { useState, useEffect } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  TextField,
  InputAdornment,
  Link,
} from '@mui/material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { AccountCircle, Logout, Refresh, Search } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function Dashboard() {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { user, logout } = useAuth();

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_BASE_URL}/data`);
      
      if (response.data.success) {
        // Add unique IDs to each row for DataGrid
        const dataWithIds = response.data.data.map((row: any, index: number) => ({
          id: index,
          ...row,
        }));
        setData(dataWithIds);
        setFilteredData(dataWithIds);
      } else {
        setError('Failed to fetch data');
      }
    } catch (error: any) {
      console.error('Error fetching data:', error);
      setError(error.response?.data?.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const text = event.target.value;
    setSearchText(text);
    const filtered = data.filter((row: any) => {
      return (
        (row.ConfNumber || '').toString().toLowerCase().includes(text.toLowerCase()) ||
        (row.Status || '').toString().toLowerCase().includes(text.toLowerCase()) ||
        (row.GuestFirstName || '').toString().toLowerCase().includes(text.toLowerCase()) ||
        (row.GuestLastName || '').toString().toLowerCase().includes(text.toLowerCase()) ||
        (row.ServiceType || '').toString().toLowerCase().includes(text.toLowerCase()) ||
        (row.AirportCode || '').toString().toLowerCase().includes(text.toLowerCase()) ||
        (row.ServiceDate || '').toString().toLowerCase().includes(text.toLowerCase()) ||
        (row.DepFlightTime || '').toString().toLowerCase().includes(text.toLowerCase()) ||
        (row.DepAirlineCode || '').toString().toLowerCase().includes(text.toLowerCase()) ||
        (row.DepFlightNumber || '').toString().toLowerCase().includes(text.toLowerCase()) ||
        (row.DepAirlineRef || '').toString().toLowerCase().includes(text.toLowerCase()) ||
        (row.DepTerminal || '').toString().toLowerCase().includes(text.toLowerCase()) ||
        (row.Pax || '').toString().includes(text) ||
        (row.RemainingPassengers || '').toString().toLowerCase().includes(text.toLowerCase()) ||
        (row.TravelerId || '').toString().toLowerCase().includes(text.toLowerCase()) ||
        (row.BookingId || '').toString().toLowerCase().includes(text.toLowerCase())
      );
    });
    setFilteredData(filtered);
  };

  // Generate columns dynamically based on data
  const columns: GridColDef[] = [
    {
      field: 'ConfNumber',
      headerName: 'Conf #',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Link
          component="button"
          variant="body2"
          onClick={() => {
            alert('Not implemented');
          }}
          sx={{
            cursor: 'pointer',
            color: 'primary.main',
            textDecoration: 'underline',
            '&:hover': {
              textDecoration: 'none',
            },
          }}
        >
          {params.value}
        </Link>
      ),
    },
    {
      field: 'Status',
      headerName: 'Status',
      flex: 1,
      minWidth: 130,
      valueGetter: (params) => {
        const status = params;
        switch (status) {
          case 'Booked':
            return 'Booked';
          case 'ProviderConfirmed':
          case 'GreeterConfirmed':
            return 'Confirmed';
          case 'ProviderNotConfirmed':
            return 'Booked';
          default:
            return 'Cancelled';
        }
      },
    },
    {
      field: 'GuestFirstName',
      headerName: 'First Name',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'GuestLastName',
      headerName: 'Last Name',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'ServiceType',
      headerName: 'Service Type',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'AirportCode',
      headerName: 'Airport',
      flex: 1,
      minWidth: 100,
    },
    {
      field: 'ServiceDate',
      headerName: 'Service Date',
      flex: 1,
      minWidth: 150,
      type: 'date',
      valueGetter: (params) => {
        return params ? new Date(params) : null;
      },
    },
    {
      field: 'DepFlightTime',
      headerName: 'Service Time',
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => {
        if (!params) return '';
        const date = new Date(params);
        return date.toLocaleString();
      },
    },
    {
      field: 'DepAirlineCode',
      headerName: 'Airline',
      flex: 1,
      minWidth: 100,
    },
    {
      field: 'DepFlightNumber',
      headerName: 'Flight No.',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'DepAirlineRef',
      headerName: 'Airline Ref No.',
      flex: 1,
      minWidth: 140,
    },
    {
      field: 'DepTerminal',
      headerName: 'Terminal',
      flex: 1,
      minWidth: 100,
    },
    {
      field: 'Pax',
      headerName: 'No. of Pax',
      flex: 1,
      minWidth: 110,
      type: 'number',
    },
    {
      field: 'RemainingPassengers',
      headerName: 'Additional Passengers',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'TravelerId',
      headerName: 'Traveller ID',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'BookingId',
      headerName: 'Booking ID',
      flex: 1,
      minWidth: 120,
    },
  ];

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header */}
      <AppBar position="static" elevation={1}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Royal FIFA Dashboard
          </Typography>
          
          <Button
            color="inherit"
            onClick={fetchData}
            startIcon={<Refresh />}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 1 }}>
              Welcome, {user?.username}
            </Typography>
            <IconButton
              size="large"
              edge="end"
              aria-label="account menu"
              aria-controls="account-menu"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              color="inherit"
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="account-menu"
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              onClick={handleMenuClose}
            >
              <MenuItem onClick={handleLogout}>
                <Logout fontSize="small" sx={{ mr: 1 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={2} sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Royal FIFA Reservations
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchText}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ height: 600, width: '100%' }}>
              <DataGrid
                rows={filteredData}
                columns={columns}
                pageSizeOptions={[10, 25, 50, 100]}
                disableRowSelectionOnClick
                sx={{
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid #f0f0f0',
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f5f5f5',
                    fontWeight: 600,
                  },
                }}
                slots={{
                  toolbar: GridToolbar,
                }}
              />
            </Box>
          )}
        </Paper>
      </Container>
    </Box>
  );
}

export default Dashboard; 